/* eslint-disable no-unreachable */
import React, { useMemo, useState, useEffect } from 'react';
import { FormControl, FormLabel } from '@mui/material';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { tryDecodeFilePath, yearFolderOptions } from '@/utils/constants';
import {
	useGetDealPoliciesQuery,
	useUpdateDealPoliciesMutation,
	useUpdateDealRowMutation,
} from '@/features/deals/dealsApi';
import { useObjectContext } from '@/hooks/useObjectContext';
import { YearFolderSelect } from '@/components/selects/YearFolderSelect';
import {
	useAddFolderToSharepointMutation,
	useLazySearchFoldersAndFilesQuery,
	useMoveSharepointItemMutation,
} from '@/features/msGraph/msGraphApi';
import { useClientStorageHooks } from '@/hooks/useClientStorageHooks';
import { getDealClientId } from '@/features/deals/helper';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';

export const YearFolderPicker = ({ minimal = false }) => {
	const { dealRow } = useObjectContext();
	const [updateDealRow] = useUpdateDealRowMutation();
	const [searchFoldersAndFiles] = useLazySearchFoldersAndFilesQuery();

	const dispatch = useDispatch();
	const [selectedYear, setSelectedYear] = useState(dealRow?.yearFolder);

	useEffect(() => {
		if (dealRow?.yearFolder) {
			setSelectedYear(dealRow.yearFolder);
		}
	}, [dealRow]);

	const [showConfirmDialog, setShowConfirmDialog] = useState(false);
	const [isUpdating, setIsUpdating] = useState(false);
	const [driveId, setDriveId] = useState(null);

	const [moveSharepointItem] = useMoveSharepointItemMutation();
	const { dealId } = useParams();

	const { data: policyData } = useGetDealPoliciesQuery({ dealId }, { skip: !dealId });

	const [updateDealPolicies] = useUpdateDealPoliciesMutation();

	const [createSharepointFolder] = useAddFolderToSharepointMutation();
	const { getClientDriveId } = useClientStorageHooks(dealRow?.brokerGroupId);

	useEffect(() => {
		const fetchDriveId = async () => {
			try {
				const clientId = getDealClientId(dealRow);
				if (clientId) {
					const { driveId: fetchedDriveId } = await getClientDriveId(clientId);
					setDriveId(fetchedDriveId);
				}
			} catch (error) {
				console.error('Error fetching drive ID:', error);
				setDriveId(null);
			}
		};
		fetchDriveId();
	}, [dealRow?.clientDocId, dealRow]);

	const handleYearChange = (option) => {
		setSelectedYear(option);
		setShowConfirmDialog(true);
	};

	const handleConfirm = async () => {
		setIsUpdating(true);
		try {
			if (!dealRow?.clientFolderDriveId || !driveId) {
				console.log(
					'client folder drive: ',
					!dealRow?.clientFolderDriveId,
					'drive Id: ',
					driveId,
				);

				dispatch(
					showToast({
						message: `Failed to move folder into: "${selectedYear}".`,
						error: true,
					}),
				);
				return;
			}
			console.log(
				'client folder drive: ',
				dealRow?.clientFolderDriveId,
				'drive Id: ',
				driveId,
			);
			// First attempt to find year folder
			let yearResponse = await searchFoldersAndFiles({
				itemId: driveId,
				driveId: dealRow.clientFolderDriveId,
				searchText: selectedYear,
				exact: true,
			}).unwrap();
			console.log('search year res: ', yearResponse);
			// Only create folders if year folder doesn't exist
			if (yearResponse?.folders?.length === 0) {
				console.log(`Year folder "${selectedYear}" not found - creating...`);
				const createdYearRes = await createSharepointFolder({
					driveId: dealRow.clientFolderDriveId,
					itemId: driveId,
					folderName: selectedYear,
				}).unwrap();
				console.log('Created year folder:', createdYearRes);
				const createdPipelineRes = await createSharepointFolder({
					driveId: dealRow.clientFolderDriveId,
					itemId: createdYearRes?.id,
					folderName: dealRow.pipelineFolder,
				}).unwrap();
				console.log('Created year folder:', createdPipelineRes);
				yearResponse = await searchFoldersAndFiles({
					itemId: driveId,
					driveId: dealRow.clientFolderDriveId,
					searchText: selectedYear,
					exact: true,
				}).unwrap();
			}

			const yearFolder = yearResponse.folders[0];
			// Search for pipeline folder
			let pipelineRes = await searchFoldersAndFiles({
				itemId: yearFolder.id,
				driveId: dealRow.clientFolderDriveId,
				searchText: dealRow.pipelineFolder,
				exact: true,
			}).unwrap();

			if (pipelineRes?.folders?.length === 0) {
				pipelineRes = await createSharepointFolder({
					driveId: dealRow.clientFolderDriveId,
					itemId: yearFolder?.id,
					folderName: dealRow.pipelineFolder,
				}).unwrap();
				console.log('Created year folder:', pipelineRes);
			}

			const dealResponse = await searchFoldersAndFiles({
				itemId: pipelineRes?.id ?? pipelineRes?.folders[0].id,
				driveId: dealRow.clientFolderDriveId,
				searchText: dealRow.dealName.trim(),
				exact: true,
			}).unwrap();

			if (dealResponse.folders?.length > 0) {
				dispatch(
					showToast({
						message: `Cannot move to ${selectedYear}: "${dealRow.dealName}" already exists in this folder.`,
						error: true,
					}),
				);
				return;
			}

			// Update deal properties
			await updateDealRow({
				dealId: dealRow.dealId,
				docId: dealRow.id,
				properties: {
					yearFolder: selectedYear,
					yearRoot: dealRow.yearRoot.replace(
						new RegExp(`${dealRow.yearFolder}$`),
						selectedYear,
					),
					...(dealRow?.dealFolderPath && {
						dealFolderPath: dealRow.dealFolderPath.replace(
							new RegExp(`${dealRow?.yearFolder}(?=\\/${dealRow?.pipelineFolder})`),
							selectedYear,
						),
					}),
					...(dealRow?.dealFolderUrl && {
						dealFolderUrl: dealRow.dealFolderUrl.replace(
							new RegExp(
								`${encodeURIComponent(dealRow.yearFolder)}(?=%2F${encodeURIComponent(dealRow.pipelineFolder)})`,
							),
							encodeURIComponent(selectedYear),
						),
					}),
				},
			}).unwrap();

			// Move the folder
			await moveSharepointItem({
				driveId: dealRow.clientFolderDriveId,
				itemId: dealRow?.driveId,
				newParentId: pipelineRes?.id ?? pipelineRes?.folders[0].id,
			}).unwrap();

			// Update policies if they exist but exclude debtors
			if (
				policyData?.policies?.length > 0 &&
				dealRow?.pipeline !== process.env.REACT_APP_PIPELINE_DEBTORS
			) {
				await updateDealPolicies({
					dealId: dealId,
					clientId: getDealClientId(dealRow),
				}).unwrap();
			}

			dispatch(
				showToast({
					message: `Successfully moved ${dealRow.dealName} to ${selectedYear} folder.`,
					success: true,
				}),
			);
		} catch (error) {
			console.error('Error updating year folder:', error);
			dispatch(
				showToast({
					message: error.message || 'Failed to update year folder',
					error: true,
				}),
			);
		} finally {
			setIsUpdating(false);
			setShowConfirmDialog(false);
		}
	};

	const handleClose = () => {
		setShowConfirmDialog(false);
		setSelectedYear(dealRow?.yearFolder);
	};

	return (
		<FormControl fullWidth>
			<FormLabel>Year Folder</FormLabel>
			<YearFolderSelect
				value={selectedYear}
				onChange={handleYearChange}
				disabled={isUpdating}
				minimal={minimal}
			/>
			<ConfirmDialog
				openDialog={showConfirmDialog}
				handleClose={handleClose}
				handleConfirm={handleConfirm}
				actionName={null}
				title='Are you sure?'
				additionalContent={
					<>
						Changing the deal year from <b>{dealRow?.yearFolder}</b> will move all
						folders and files saved to this deal to the <b>{selectedYear}</b> folder.
					</>
				}
				canBeUndone={true}
				isLoading={isUpdating}
			/>
		</FormControl>
	);
};
